// src/hooks/useProjectPermissions.js
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axiosInstance from "../axiosInstace";

/**
 * role -> permissions mapping (mirror server mapping)
 * Keep this in sync with backend if it changes.
 */
const ROLE_PERMISSIONS = {
  APPROVER: [
    "PROJECT_VIEW",
    "PROJECT_REMOVE_MEMBER",
    "PROJECT_UPDATE",
    "PROJECT_DELETE",
    "TASK_CREATE",
    "TASK_UPDATE",
    "TASK_VIEW",
    "TASK_DELETE",
    "TODO_CREATE",
    "TODO_UPDATE",
    "TODO_VIEW",
    "TODO_DELETE",
    "TODOLIST_CREATE",
    "TODOLIST_UPDATE",
    "TODOLIST_VIEW",
    "TODOLIST_DELETE",
  ],
  CREATOR: [
    "PROJECT_VIEW",
    "PROJECT_UPDATE",
    "TASK_CREATE",
    "TASK_VIEW",
    "TASK_UPDATE",
    "TASK_DELETE",
    "TODO_CREATE",
    "TODO_UPDATE",
    "TODO_VIEW",
    "TODO_DELETE",
    "TODOLIST_CREATE",
    "TODOLIST_UPDATE",
    "TODOLIST_VIEW",
    "TODOLIST_DELETE",
  ],
  DOER: [
    "PROJECT_VIEW",
    "TASK_CREATE",
    "TASK_VIEW",
    "TASK_UPDATE",
    "TASK_DELETE",
    "TODO_CREATE",
    "TODO_UPDATE",
    "TODO_VIEW",
    "TODO_DELETE",
    "TODOLIST_CREATE",
    "TODOLIST_UPDATE",
    "TODOLIST_VIEW",
    "TODOLIST_DELETE",
  ],
  VIEWER: ["PROJECT_VIEW", "TASK_VIEW", "TODO_VIEW", "TODOLIST_VIEW"],
  CLIENT: ["PROJECT_VIEW", "TASK_VIEW", "TODO_VIEW", "TODOLIST_VIEW"],
};

function getStoredEmpId() {
  try {
    const raw = sessionStorage.getItem("user");
    if (!raw) return null;
    const u = JSON.parse(raw);
    return u?.empId ?? null;
  } catch {
    return null;
  }
}

export default function useProjectPermissions(projectId, options = {}) {
  const { userEmpId: userEmpIdOpt = null } = options;

  const userEmpId = userEmpIdOpt ?? getStoredEmpId();

  // ---------------- STATE ----------------
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState(null);
  const [permError, setPermError] = useState(false);

  // ---------------- REFS ----------------
  const fetchTriggerRef = useRef(0);
  const mountedRef = useRef(true);
  const prevProjectIdRef = useRef(null);

  // mounted guard
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ---------------- CORE FETCH EFFECT ----------------
  useEffect(() => {
    // 🔥 RESET STATE WHEN PROJECT CHANGES
    if (prevProjectIdRef.current !== projectId) {
      prevProjectIdRef.current = projectId;

      if (mountedRef.current) {
        setMembers([]);
        setError(null);
        setPermError(false);
        setLoading(false);
      }
    }

    // must have project + user
    if (!projectId || !userEmpId) {
      if (mountedRef.current) {
        setMembers([]);
        setLoading(false);
        setError(null);
      }
      return;
    }

    // do not auto retry on permission failure
    if (permError) {
      if (mountedRef.current) setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchMembers = async () => {
      if (!mountedRef.current) return;

      setLoading(true);
      setError(null);

      try {
        const res = await axiosInstance.get(
          `/api/emp/projects/projectmemberaccess/${projectId}/allmembers`,
        );

        if (cancelled || !mountedRef.current) return;

        const list = Array.isArray(res?.data?.data) ? res.data.data : [];

        setMembers((prev) => {
          if (prev.length === list.length) {
            const same = prev.every(
              (p, i) => String(p?.empId) === String(list[i]?.empId),
            );
            if (same) return prev;
          }
          return list;
        });

        setError(null);
      } catch (err) {
        if (cancelled || !mountedRef.current) return;

        console.error("[useProjectPermissions] fetch failed", err);

        setMembers([]);
        setError(err);
        setPermError(true); // stop auto retry
      } finally {
        if (!cancelled && mountedRef.current) setLoading(false);
      }
    };

    fetchMembers();

    return () => {
      cancelled = true;
    };
  }, [projectId, userEmpId, permError]);

  // ---------------- DERIVED DATA ----------------
  const member = useMemo(() => {
    if (!members || !userEmpId) return null;
    return members.find((m) => String(m.empId) === String(userEmpId)) ?? null;
  }, [members, userEmpId]);

  const permissions = useMemo(() => {
    const set = new Set();
    const role = (member?.roleType || "").toUpperCase();
    if (ROLE_PERMISSIONS[role]) {
      ROLE_PERMISSIONS[role].forEach((p) => set.add(p));
    }
    return set;
  }, [member]);

  const can = useCallback(
    (perm) => {
      if (!perm) return false;
      return permissions.has(perm);
    },
    [permissions],
  );

  // ---------------- MANUAL REFRESH ----------------
  const refresh = useCallback(() => {
    if (mountedRef.current) setPermError(false);
    fetchTriggerRef.current += 1;
    return Promise.resolve();
  }, []);

  // ---------------- RETURN ----------------
  return useMemo(
    () => ({
      loading,
      error,
      members,
      member,
      permissions,
      can,
      refresh,
      permError,
    }),
    [loading, error, members, member, permissions, can, refresh, permError],
  );
}
